import { useEffect, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Mail, Send, TrendingDown, TrendingUp, Users, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Skeleton, inputCls, btnPrimary } from "@/components/portal/ui";
import { Switch } from "@/components/ui/switch";
import { AdminTitle } from "@/pages/admin/AdminLayout";
import { monthLabel, shiftMonth } from "@/pages/portal/Leaderboard";

const thisMonth = () => new Date().toISOString().slice(0, 7);

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm" data-testid={`community-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-1 text-2xl font-black text-slate-900 tabular-nums">{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Row({ r }) {
  const [open, setOpen] = useState(false);
  const up = r.growth > 0;
  return (
    <div className="border-b border-slate-50 last:border-0" data-testid={`community-report-row-${r.id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full text-left py-3 flex items-center gap-3 hover:bg-slate-50/60 transition px-1 rounded-xl">
        <span className={`shrink-0 w-9 h-9 rounded-xl grid place-items-center ${r.type === "tts" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
          <UsersRound className="w-4 h-4" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-slate-900 truncate">{r.name} <span className="text-[10px] font-black text-slate-400">{r.type.toUpperCase()}</span></span>
          <span className="block text-[11px] text-slate-500 truncate">Leader {r.leader} · {r.meetings} pertemuan · kehadiran {r.attendance_rate}%</span>
        </span>
        <span className="shrink-0 text-right">
          <span className="block text-sm font-black text-slate-900 tabular-nums">{r.members}</span>
          <span className={`block text-[11px] font-bold inline-flex items-center gap-0.5 ${up ? "text-emerald-600" : r.growth < 0 ? "text-red-500" : "text-slate-400"}`}>
            {up ? <TrendingUp className="w-3 h-3" /> : r.growth < 0 ? <TrendingDown className="w-3 h-3" /> : null}{r.growth > 0 ? `+${r.growth}` : r.growth}
          </span>
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-4 pl-12 pr-1 space-y-2" data-testid={`community-report-detail-${r.id}`}>
          <p className="text-[11px] text-slate-500">Anggota baru bulan ini <b className="text-slate-800">{r.joined}</b> · keluar/pindah <b className="text-slate-800">{r.left}</b> · rata-rata hadir <b className="text-slate-800">{r.avg_present}</b> orang</p>
          {r.detail.length === 0 ? (
            <p className="text-xs text-slate-400">Belum ada pertemuan pada bulan ini.</p>
          ) : r.detail.map((m) => (
            <div key={m.id} className="flex items-center gap-3 text-xs bg-slate-50 rounded-xl px-3 py-2">
              <span className="flex-1 min-w-0 truncate font-semibold text-slate-700">{m.title}</span>
              <span className="text-slate-400 tabular-nums">{m.date}</span>
              <span className="font-bold text-slate-900 tabular-nums">{m.present}/{m.registered}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DigestCard({ month }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/communities/digest").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/communities/digest", { enabled: cfg.enabled, extra_emails: cfg.extra_emails });
      setCfg(data);
      toast.success("Pengaturan laporan email disimpan");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/communities/digest/send?month=${month}`);
      toast.success(`Ringkasan ${month} terkirim ke ${data.sent} penerima`);
      const { data: fresh } = await api.get("/admin/communities/digest");
      setCfg(fresh);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!cfg) return <Skeleton className="h-40" />;
  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm space-y-4" data-testid="community-digest-card">
      <div>
        <p className="font-extrabold text-slate-900 flex items-center gap-2"><Mail className="w-4 h-4 text-indigo-600" />Laporan Bulanan ke Email Gembala</p>
        <p className="text-[11px] text-slate-500 mt-0.5">Terkirim otomatis setiap tanggal 1 pukul 07:30 WITA berisi rekap bulan sebelumnya.</p>
      </div>
      <label className="flex items-center gap-3 text-sm font-bold text-slate-700">
        <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="digest-enabled-switch" />
        Kirim otomatis setiap awal bulan
      </label>
      <div>
        <p className="text-xs font-bold text-slate-600 mb-1">Email tambahan (pisahkan dengan koma)</p>
        <textarea rows={2} className={inputCls} placeholder="gembala@gereja.com, sekretariat@gereja.com"
          value={cfg.extra_emails} onChange={(e) => setCfg({ ...cfg, extra_emails: e.target.value })} data-testid="digest-emails-input" />
        <p className="text-[11px] text-slate-500 mt-1" data-testid="digest-recipients">
          Penerima saat ini ({cfg.recipients.length}): {cfg.recipients.join(", ") || "belum ada — tambahkan email di atas"}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={busy} className={btnPrimary} data-testid="digest-save-button">Simpan</button>
        <button onClick={sendNow} disabled={busy} data-testid="digest-send-button"
          className="text-sm font-bold rounded-xl px-5 py-3 bg-slate-100 text-slate-700 hover:bg-slate-200 transition inline-flex items-center gap-2">
          <Send className="w-4 h-4" />Kirim {monthLabel(month)} Sekarang
        </button>
      </div>
      {cfg.last_sent_at && (
        <p className="text-[11px] text-slate-400" data-testid="digest-last-sent">
          Terakhir dikirim {cfg.last_sent_at.slice(0, 16).replace("T", " ")} untuk {monthLabel(cfg.last_month)} → {cfg.last_recipients.length} penerima.
        </p>
      )}
    </div>
  );
}

export default function CommunityReport() {
  const [month, setMonth] = useState(thisMonth);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setData(null);
    api.get(`/admin/communities/report?month=${month}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month]);

  const rows = (data?.rows || []).filter((r) => filter === "all" || r.type === filter);

  return (
    <div className="space-y-5" data-testid="admin-community-report">
      <AdminTitle title="Laporan Komunitas" subtitle="Rekap anggota, pertumbuhan & kehadiran tiap RC dan TTS Community" />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1 bg-white rounded-full p-1 border border-slate-200">
          {[["all", "Semua"], ["rc", "RC"], ["tts", "TTS"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`community-filter-${k}`}
              className={`text-xs font-bold rounded-full px-4 py-1.5 transition ${filter === k ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMonth((m) => shiftMonth(m, -1))} data-testid="community-month-prev" className="w-8 h-8 rounded-lg grid place-items-center text-slate-500 hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-xs font-bold text-slate-600 tabular-nums whitespace-nowrap" data-testid="community-month-label">{monthLabel(month)}</span>
          <button disabled={month >= thisMonth()} onClick={() => setMonth((m) => shiftMonth(m, 1))} data-testid="community-month-next" className="w-8 h-8 rounded-lg grid place-items-center text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {!data ? <Skeleton className="h-72" /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Total Anggota" value={data.totals.members} sub={`${data.rows.length} komunitas`} />
            <Stat label="Anggota Baru" value={data.totals.joined} sub={`keluar/pindah ${data.totals.left}`} />
            <Stat label="Pertemuan" value={data.totals.meetings} sub={`${data.totals.registered} pendaftar`} />
            <Stat label="Kehadiran" value={`${data.totals.attendance_rate}%`} sub={`${data.totals.present} hadir terpindai`} />
          </div>

          <div className="bg-white rounded-3xl p-4 md:p-6 border border-slate-100 shadow-sm">
            <p className="font-extrabold text-slate-900 flex items-center gap-2 px-1"><Users className="w-4 h-4 text-blue-600" />Per Komunitas</p>
            <p className="text-[11px] text-slate-500 px-1 mt-0.5">Ketuk baris untuk melihat rincian pertemuan bulan ini.</p>
            <div className="mt-3">
              {rows.map((r) => <Row key={r.id} r={r} />)}
              {rows.length === 0 && <p className="py-6 text-center text-sm text-slate-500" data-testid="community-report-empty">Belum ada data komunitas.</p>}
            </div>
          </div>
          {data.pending_requests > 0 && (
            <p className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3" data-testid="community-pending-note">
              {data.pending_requests} pendaftaran menunggu persetujuan leader komunitas.
            </p>
          )}
          <DigestCard month={month} />
        </>
      )}
    </div>
  );
}
